import { ApiError, apiGet, apiSend } from '../../api/client';
import { DEMO_PRIZE_WEIGHTS, PRIZE_AZN, pickSegmentIndex, sanitizePrizeAmount } from './wheelConfig';
import type { PrizeId, SpinResult, WheelGrantStatus, WheelStatus } from './wheelTypes';

function pickDemoPrize(): PrizeId {
  const total = DEMO_PRIZE_WEIGHTS.reduce((sum, row) => sum + row.weight, 0);
  let cursor = Math.random() * total;
  for (const row of DEMO_PRIZE_WEIGHTS) {
    cursor -= row.weight;
    if (cursor <= 0) return row.prizeId;
  }
  return 'azn5';
}

function demoResult(): SpinResult {
  const prizeId = pickDemoPrize();
  const amountAzn = sanitizePrizeAmount(PRIZE_AZN[prizeId]);
  return {
    prizeId,
    amountAzn,
    segmentIndex: pickSegmentIndex(prizeId),
    demo: true,
    credited: false,
    testMode: false,
    ledger: null,
    withdrawalEligible: false,
  };
}

export const DEMO_WHEEL_STATUS: WheelStatus = {
  available: true,
  nextAvailableAt: null,
  demo: true,
  lastPrizeId: null,
  lastAmountAzn: null,
  testMode: false,
  ledger: null,
  withdrawalEligible: false,
};

function asPrizeId(value: unknown): PrizeId {
  return typeof value === 'string' && value in PRIZE_AZN ? (value as PrizeId) : 'azn5';
}

function normalizeGrants(raw: unknown): WheelGrantStatus[] {
  if (!Array.isArray(raw)) return [];
  const out: WheelGrantStatus[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const g = row as Record<string, unknown>;
    const id = Number(g.id);
    if (!Number.isFinite(id)) continue;
    out.push({
      id,
      awarded_minor: Number(g.awarded_minor ?? 0) || 0,
      grant_balance_minor: Number(g.grant_balance_minor ?? g.awarded_minor ?? 0) || 0,
      wagering_multiplier: Number(g.wagering_multiplier ?? 30) || 30,
      wagering_target_minor: Number(g.wagering_target_minor ?? g.wagering_required_minor ?? 0) || 0,
      wagering_completed_minor: Number(g.wagering_completed_minor ?? 0) || 0,
      wagering_remaining_minor: Number(g.wagering_remaining_minor ?? 0) || 0,
      status: String(g.status || 'NONE'),
      expires_at: (g.expires_at as string | null | undefined) ?? null,
      currency: typeof g.currency === 'string' ? g.currency : undefined,
    });
  }
  return out;
}

function normalizeStatus(payload: Partial<WheelStatus> & Record<string, unknown>): WheelStatus {
  const lastPrizeId = asPrizeId(payload.lastPrizeId ?? payload.last_prize_id ?? 'azn5');
  const rawLast = payload.lastAmountAzn ?? payload.last_amount_azn;
  const testMode = Boolean(payload.testMode ?? payload.test_mode);
  const rawWagering = (payload.wagering as Record<string, unknown> | null | undefined) ?? null;
  const wagering = rawWagering
    ? {
        multiplier: Number(rawWagering.multiplier ?? 30) || 30,
        bonus_balance_minor: Number(rawWagering.bonus_balance_minor ?? 0) || 0,
        wagering_target_minor: Number(rawWagering.wagering_target_minor ?? 0) || 0,
        wagering_completed_minor: Number(rawWagering.wagering_completed_minor ?? 0) || 0,
        wagering_remaining_minor: Number(rawWagering.wagering_remaining_minor ?? 0) || 0,
        status: String(rawWagering.status || rawWagering.bonus_status || 'NONE'),
        bonus_withdrawal_eligible: Boolean(rawWagering.bonus_withdrawal_eligible),
        grants: normalizeGrants(rawWagering.grants),
      }
    : null;
  return {
    available: Boolean(payload.available),
    nextAvailableAt:
      (payload.nextAvailableAt as string | null | undefined) ??
      (payload.next_available_at as string | null | undefined) ??
      null,
    demo: payload.demo !== false && !testMode,
    lastPrizeId: rawLast == null ? null : lastPrizeId,
    lastAmountAzn: rawLast == null ? null : sanitizePrizeAmount(Number(rawLast)),
    wagering,
    testMode,
    ledger: (payload.ledger as string | null | undefined) ?? null,
    testBalanceMinor: Number(payload.testBalanceMinor ?? payload.test_balance_minor ?? 0) || 0,
    testExpiresAt:
      (payload.testExpiresAt as string | null | undefined) ??
      (payload.test_expires_at as string | null | undefined) ??
      null,
    withdrawalEligible: false,
    requiresLogin: Boolean(payload.requiresLogin ?? payload.requires_login),
  };
}

export function isLiveTestWheel(status: WheelStatus | SpinResult | null | undefined): boolean {
  return Boolean(status && status.testMode && status.demo === false);
}

/** True for real bonus credits or isolated live test ledger (never client-side demo). */
export function isServerBackedWheel(status: WheelStatus | SpinResult | null | undefined): boolean {
  return Boolean(status && status.demo === false);
}

export async function fetchWheelStatus(): Promise<WheelStatus> {
  try {
    const payload = await apiGet<Partial<WheelStatus> & Record<string, unknown>>('/api/v1/lucky-wheel/status');
    return normalizeStatus(payload);
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      // Guests see the wheel UI but cannot run client-side fake credited spins.
      return {
        ...DEMO_WHEEL_STATUS,
        available: false,
        demo: false,
        requiresLogin: true,
      };
    }
    return { ...DEMO_WHEEL_STATUS, available: false, demo: false, requiresLogin: true };
  }
}

export async function requestWheelSpin(live: boolean): Promise<SpinResult> {
  if (!live) {
    await new Promise((resolve) => window.setTimeout(resolve, 40));
    return demoResult();
  }
  const payload = await apiSend<SpinResult & Record<string, unknown>>('POST', '/api/v1/lucky-wheel/spin', {});
  const prizeId = asPrizeId(payload.prizeId ?? payload.prize_id);
  const amountAzn = sanitizePrizeAmount(Number(payload.amountAzn ?? payload.amount_azn) || PRIZE_AZN[prizeId]);
  return {
    prizeId,
    amountAzn,
    segmentIndex: Number(payload.segmentIndex ?? payload.segment_index) || pickSegmentIndex(prizeId),
    demo: false,
    credited: payload.credited !== false,
    testMode: Boolean(payload.testMode ?? payload.test_mode),
    ledger: (payload.ledger as string | null | undefined) ?? 'TEST_BONUS',
    testBalanceMinor: Number(payload.testBalanceMinor ?? payload.test_balance_minor ?? 0) || 0,
    testExpiresAt:
      (payload.testExpiresAt as string | null | undefined) ??
      (payload.test_expires_at as string | null | undefined) ??
      null,
    withdrawalEligible: false,
    replayed: Boolean(payload.replayed),
  };
}
